/**
 * ZoneAndSchoolBody
 * 
 * @Author: Focci
 * @Date: 2023-07-25 13:40:00
 * @Last Modified by: Focci
 * @Last Modified time: 2023-07-25 13:40:00
 */

import Tab from '@comp/Tab'
import Scrollbar from '@comp/Scrollbar'
import { chineseCheck } from '@i18n/config'
import { isArray, map, forEach, filter, omit } from 'lodash-es'
import { useLocale, useTranslations } from 'next-intl'
import { cookieGet, cookieSet, isValid } from '@lib/utils'
import { HG_CURRENT_REGION_INFO } from '@lib/config'
import { 
  useState, useMemo, useCallback, forwardRef, useImperativeHandle
} from 'react'
import RegionTriger from './RegionTriger'
import MenuList from './MenuList'
import Suburbs from './Suburbs'
import SchoolList from './SchoolList'

function getOptions(data, locale, type = 'region') {
  if(!isArray(data)) {
    return []
  }

  return map(data, (item) => {
    const label = chineseCheck(locale) ? item.name : item.en_name

    const tmp = {
      type,
      slug: item.slug,
      lat: parseFloat(item.latitude),
      lng: parseFloat(item.longitude),
      label,
      value: isValid(item[`${type}_id`]) ? item[`${type}_id`] : item.id
    }

    if(type === 'region') {
      tmp.districts = isArray(item.districts) 
        ? getOptions(item.districts, locale, 'district')
        : []
    } else if(type === 'district') {
      tmp.suburbs = isArray(item.suburbs) 
        ? getOptions(item.suburbs, locale, 'suburb')
        : []
    }

    return tmp
  })
}

function ZoneAndSchoolBody({
  type,
  single,
  module,
  setType,
  data = [],
  onSchoolSubmit = () => {}
}, ref) {
  const locale = useLocale()
  const ts = useTranslations('search')
  const tabOptions = useMemo(() => {
    const tmp = []
    module.indexOf('district') > -1 && tmp.push(
      { label: ts('district'), value: 'district' }
    )
    module.indexOf('school') > -1 && tmp.push(
      { label: ts('schools'), value: 'school' }
    )

    return tmp
  }, [module, ts])

  const region = useMemo(() => getOptions(data, locale), [data, locale])
  const defaultRegionId = useMemo(() => (
    cookieGet(HG_CURRENT_REGION_INFO, true)?.regionId || region[0]?.value
  ), [region])
  
  const district = useMemo(() => (
    filter(region, (f) => f.value === defaultRegionId)[0]?.districts || []
  ), [defaultRegionId, region])
  
  const [allChecked, setAllChecked] = useState({})
  const [regionId, setRegionId] = useState(defaultRegionId)
  const [districtId, setDistrictId] = useState(district?.[0]?.value)
  const [suburbIds, setSuburbIds] = useState([])
  const [regionOption] = useState(region)
  const [districtOption, setDistrictOption] = useState(district)
  const [suburbOption, setSuburbOption] = useState(district?.[0]?.suburbs)

  const reset = useCallback(() => {
    setSuburbIds([])
    setAllChecked({})
  }, [])

  // 获得选中suburb的数量
  const badges = useMemo(() => {
    const result = {}
    forEach(allChecked, (item, key) => {
      const len = item.length
      if(isArray(item) && len > 0) {
        result[key] = len
      }
    })

    return result
  }, [allChecked])

  // region change
  const handleRegionChange = useCallback(({ value, districts }) => {
    // 保存region在本地
    cookieSet(HG_CURRENT_REGION_INFO, { regionId: value })

    setSuburbIds([])
    setAllChecked({})
    setRegionId(value)
    setDistrictId(districts[0]?.value)
    setDistrictOption(districts)
    setSuburbOption(districts[0]?.suburbs)
  }, [])

  // district change
  const handleDistrictClick = useCallback(({ value, suburbs }) => {
    setDistrictId(value)
    setSuburbIds([...(allChecked[value] ?? [])])
    setSuburbOption(suburbs)

    if(single) {
      reset()
    }
  }, [allChecked, reset, single])

  // suburb change
  const handleSuburbChange = useCallback((ids) => {
    setAllChecked((prev) => ({ ...prev, [districtId]: ids }))
  }, [districtId])

  const handleTypeChange = useCallback(({ value }) => {
    setType(value)
    reset()
  }, [reset, setType])

  /**
   * 澳洲的suburbs是通过单独接口来获取的
   * 通过此函数，将获得的数据填充到 district -> suburbs
   */
  const handleSuburbDataReady = useCallback(({ options, districtId: dId }) => {
    setDistrictOption((prev) => [
      ...map(prev, (item) => ({
        ...item,
        ...(item.value === dId ? { suburbs: options } : null)
      }))
    ])
  }, [])

  // 获取选中的数据
  const getCheckedData = useCallback(() => {
    const result = []
    forEach(allChecked, (ids, key) => {
      if(!ids || ids?.length === 0) return
      const numDistrictId = parseInt(key, 10)
      const districtArr = filter(districtOption, (f) => f.value === numDistrictId)
      const suburbs = districtArr[0]?.suburbs || []
      
      // suburb被全部选中
      if(ids.length === suburbs.length) {
        result.push(omit(districtArr[0], 'suburbs'))
      } else {
        result.push(...filter(suburbs, (f) => ids.indexOf(f.value) > -1))
      }
    })
    
    return { type, data: result }
  }, [allChecked, districtOption, type])
  
  useImperativeHandle(ref, () => ({
    reset,
    getCheckedData
  }))
  
  return (
    <div className="grid grid-cols-3 w-full -mt-2">
      <div className="col-span-1 flex flex-col">
        <div className="h-12 flex items-center border-b border-b-solid shrink-0">
          <RegionTriger 
            options={regionOption}
            value={regionId}
            onChange={handleRegionChange}
          />
        </div>
        <div className="flex-1 bg-[#F2F5F7]">
          <Scrollbar>
            <MenuList
              value={districtId}
              badges={badges}
              options={districtOption}
              onClick={handleDistrictClick}
            />
          </Scrollbar>
        </div>
      </div>
      <div className="col-span-2 flex flex-col">
        <div className="h-12 flex items-center border-b border-b-solid">
          <Tab
            options={tabOptions}
            value={type}
            wrapperClassName="gap-x-0"
            buttonClassName="rounded-md"
            className="px-3 py-1 text-f.9 font-medium rounded-md hover:bg-cf0"
            activeSign={<span />}
            onChange={handleTypeChange}
          />
        </div>
        <div className="pl-4 pt-2 flex flex-col flex-auto">
          {
            type === 'district' 
              ? (
                <Suburbs
                  single={single}
                  districtId={districtId}
                  value={suburbIds} 
                  data={suburbOption}
                  onChange={handleSuburbChange}
                  onSuburbDataReady={handleSuburbDataReady}
                />
              )
              : (
                <SchoolList 
                  districtId={districtId}
                  onChange={onSchoolSubmit}
                />
              )
          }
        </div>
      </div>
    </div>
  )
}

export default forwardRef(ZoneAndSchoolBody)
